import * as Yup from 'yup';
import { type FC } from 'react';
import { Formik } from 'formik';

import {
  Box,
  Button,
  FormHelperText,
  TextField,
  Checkbox,
  Typography,
  Link,
  FormControlLabel,
  CircularProgress,
  Stack,
  Card,
  Container,
  styled
} from '@mui/material';

import { Helmet } from 'react-helmet-async';
import useAuth from 'src/shared/auth/useAuth';
import useRefMounted from 'src/shared/helpers/useRefMounted';
import { useTranslation } from 'react-i18next';
import Logo from 'src/core/ui/helpers/logo/logoSign/Logo';
import { ENVIROMENT_NAME } from 'src/shared/environment';

const MainContent = styled(Box)(
  () => `
    height: 100%;
    display: flex;
    flex: 1;
    flex-direction: column;
`
);

const TopWrapper = styled(Box)(
  () => `
  display: flex;
  width: 100%;
  flex: 1;
  padding: 20px;
`
);

const LoginForm: FC = () => {
  const { login, isAuthenticated, loading, status: requestStatus } = useAuth();
  const isMountedRef = useRefMounted();
  const { t }: { t: any } = useTranslation();

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .max(255)
      .required(t('The username field is required')),
    password: Yup.string()
      .max(255)
      .required(t('The password field is required')),
    terms: Yup.boolean().oneOf(
      [true],
      t('You must agree to our terms and conditions')
    )
  });

  return (
    <Formik
      initialValues={{
        username: '',
        password: '',
        terms: true,
        submit: null
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        // fire login; request lifecycle is tracked via loading + requestStatus
        login(values.username, values.password);
        if (isMountedRef()) {
          setSubmitting(false); // let auth loading control the button/spinner
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values
      }) => {
        // derive a human-readable error from requestStatus
        let submitError: string | null = null;

        if (!loading && !isAuthenticated && requestStatus && requestStatus !== 'idle') {
          if (requestStatus === 'error') {
            submitError = t('Network error. Please check your connection and try again.');
          } else if (requestStatus === 'aborted') {
            submitError = t('Request was cancelled. Please try again.');
          } else if (requestStatus === 400 || requestStatus === 401) {
            submitError = t('Incorrect username or password.');
          } else if (typeof requestStatus === 'number' && requestStatus >= 500) {
            submitError = t('Server error. Please try again later.');
          } else if (requestStatus === 'loading') {
            // no message while loading
            submitError = null;
          } else {
            submitError = t('Login failed. Please try again.');
          }
        }

        return (
          <form noValidate onSubmit={handleSubmit}>
            <TextField
              error={Boolean(touched.username && errors.username)}
              fullWidth
              margin="normal"
              autoFocus
              helperText={touched.username && errors.username}
              label={t('Username')}
              name="username"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.username}
              variant="outlined"
              autoComplete="username"
            />

            <TextField
              error={Boolean(touched.password && errors.password)}
              fullWidth
              margin="normal"
              helperText={touched.password && errors.password}
              label={t('Password')}
              name="password"
              onBlur={handleBlur}
              onChange={handleChange}
              type="password"
              value={values.password}
              variant="outlined"
              autoComplete="current-password"
              // avoid label sitting on top of autofilled value
              InputLabelProps={{ shrink: true }}
            />

            <Box
              alignItems="center"
              display={{ xs: 'block', md: 'flex' }}
              justifyContent="space-between"
            >
              <Box display={{ xs: 'block', md: 'flex' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.terms}
                      name="terms"
                      color="primary"
                      onChange={handleChange}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('I accept the')}{' '}
                      <Link component="a" href="#">
                        {t('terms and conditions')}
                      </Link>
                      .
                    </Typography>
                  }
                />
              </Box>
            </Box>

            {Boolean(touched.terms && errors.terms) && (
              <FormHelperText error>{errors.terms}</FormHelperText>
            )}

            <Stack spacing={2}>
              <Button
                sx={{ mt: 3 }}
                color="primary"
                startIcon={
                  loading ? <CircularProgress size="1rem" /> : null
                }
                disabled={isSubmitting || loading}
                type="submit"
                fullWidth
                size="large"
                variant="contained"
              >
                {t('Sign in')}
              </Button>

              {isAuthenticated && (
                <Link href="/">Go to dashboard {'>>'}</Link>
              )}

              {submitError && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {submitError}
                </FormHelperText>
              )}
            </Stack>
          </form>
        );
      }}
    </Formik>
  );
};

export function Login() {
  const { t }: { t: any } = useTranslation();
  
  return (
    <>
      <Helmet>
        <title>Login - {ENVIROMENT_NAME}</title>
      </Helmet>
      <MainContent>
        <TopWrapper>
          <Container maxWidth="sm">
            <Logo />
            <Card
              sx={{
                mt: 3,
                px: 4,
                pt: 5,
                pb: 3
              }}
            >
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    mb: 1
                  }}
                >
                  {t('Sign in')}
                </Typography>
                <Typography
                  variant="h4"
                  color="text.secondary"
                  fontWeight="normal"
                  sx={{
                    mb: 3
                  }}
                >
                  {t('Fill in the fields below to sign into your account.')}
                </Typography>
              </Box>
              <LoginForm />
            </Card>
          </Container>
        </TopWrapper>
      </MainContent>
    </>
  );
}

export default Login;
